/**
 * Topic dialog (docs/06-UI-SPEC.md §5).
 *
 * Asked once when a lesson starts and skippable, because a teacher opening a board
 * mid-call must never be blocked by a form. Skipping starts the lesson with no topic;
 * docs/02-SRS.md I3 turns that into "Untitled Topic" when the lecture is filed.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import type { GradeBand } from '@opal/shared';

import { DEFAULT_GRADE_BAND, GRADE_BAND_CHOICES, normaliseTopic } from '../lesson.js';

export interface TopicDialogProps {
  /** Called with the answers when the teacher starts the lesson. Empty topic means skipped. */
  readonly onStart: (topic: string, gradeBand: GradeBand) => void;
  /** Called when the teacher backs out with Escape or a click outside the card. */
  readonly onClose: () => void;
}

interface TopicFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly inputRef: RefObject<HTMLInputElement>;
}

function TopicField({ value, onChange, inputRef }: TopicFieldProps) {
  return (
    <label className="flex flex-col gap-2 text-sm font-medium text-text-secondary">
      Topic
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder="Fractions"
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="h-touch rounded-md border border-border bg-surface px-3 text-base text-text-primary placeholder:text-text-placeholder"
      />
    </label>
  );
}

interface GradeChoicesProps {
  readonly value: GradeBand;
  readonly onChange: (value: GradeBand) => void;
}

function GradeChoices({ value, onChange }: GradeChoicesProps) {
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="pb-2 text-sm font-medium text-text-secondary">Grade</legend>
      <div className="flex gap-3">
        {GRADE_BAND_CHOICES.map((choice) => (
          <label
            key={choice.value}
            className="flex h-touch flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 text-base text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
          >
            <input
              type="radio"
              name="grade-band"
              value={choice.value}
              checked={choice.value === value}
              onChange={() => {
                onChange(choice.value);
              }}
              className="accent-primary"
            />
            {choice.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function DialogActions({ onSkip }: { readonly onSkip: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 pt-2">
      <button
        type="button"
        onClick={onSkip}
        className="h-touch rounded-md border border-border bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
      >
        Skip
      </button>
      <button
        type="submit"
        className="h-touch rounded-md bg-primary px-6 text-base font-medium text-text-inverse transition duration-fast ease-out hover:bg-primary-hover active:bg-primary-active"
      >
        Start Lesson
      </button>
    </div>
  );
}

/** Focuses the topic box on open and closes the dialog on Escape. */
function useDialogKeys(onClose: () => void, inputRef: RefObject<HTMLInputElement>): void {
  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, inputRef]);
}

export function TopicDialog({ onStart, onClose }: TopicDialogProps) {
  const [topic, setTopic] = useState('');
  const [gradeBand, setGradeBand] = useState<GradeBand>(DEFAULT_GRADE_BAND);
  const topicInput = useRef<HTMLInputElement>(null);

  useDialogKeys(onClose, topicInput);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="topic-dialog-title"
        className="flex w-full max-w-[440px] flex-col gap-6 rounded-lg bg-surface p-8 shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          onStart(normaliseTopic(topic), gradeBand);
        }}
      >
        <h2 id="topic-dialog-title" className="text-xl font-semibold text-text-primary">
          What are you teaching today?
        </h2>
        <TopicField value={topic} onChange={setTopic} inputRef={topicInput} />
        <GradeChoices value={gradeBand} onChange={setGradeBand} />
        <DialogActions
          onSkip={() => {
            onStart('', gradeBand);
          }}
        />
      </form>
    </div>
  );
}
